import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { motion } from 'motion/react';
import { Lock, ShieldCheck, Eye, EyeOff, CheckCircle2, ArrowRight } from 'lucide-react';

export const PasswordResetWizard: React.FC = () => {
  const { activeUser, updateMyPassword, setCurrentView } = useWBL();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsUpdating(true);
    try {
      await updateMyPassword(newPassword);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center space-y-6">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100">
          <CheckCircle2 className="w-10 h-10 text-emerald-500" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Security Updated</h2>
          <p className="text-slate-500 text-sm max-w-xs mx-auto">
            Your temporary credentials have been replaced. You now have full access to the portal.
          </p>
        </div>
        <button
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center space-x-2 px-8 py-4 bg-[#0094c8] text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-[#007ba6] transition-all shadow-lg shadow-[#0094c8]/20"
        >
          <span>Enter Portal</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
      <div className="bg-[#011d3c] p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12">
          <ShieldCheck className="w-24 h-24" />
        </div>
        <div className="relative z-10 space-y-2">
          <div className="flex items-center space-x-3 text-[#0094c8]">
            <Lock className="w-5 h-5" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">Security Protocol</span>
          </div>
          <h2 className="text-2xl font-black uppercase tracking-tight">Set Your Password</h2>
          <p className="text-slate-400 text-xs font-medium">
            To ensure account integrity, you must replace your temporary password before proceeding.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start space-x-3 animate-shake">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
            <p className="text-[11px] text-red-600 font-bold leading-relaxed">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">New Secure Password</label>
            <div className="relative">
              <input 
                type={showNewPassword ? "text" : "password"} 
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0094c8]/20 focus:border-[#0094c8] outline-none transition-all font-medium"
                placeholder="Min. 8 characters"
                required
              />
              <button 
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Confirm Password</label>
            <input 
              type="password" 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0094c8]/20 focus:border-[#0094c8] outline-none transition-all font-medium"
              placeholder="Repeat new password"
              required
            />
          </div>
        </div>

        <button 
          type="submit"
          disabled={isUpdating}
          className="w-full py-4 bg-[#011d3c] text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-[#022a57] transition-all flex items-center justify-center space-x-2 disabled:opacity-50 shadow-lg shadow-slate-900/10"
        >
          {isUpdating ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Updating Access...</span>
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              <span>Finalize Credentials</span>
            </>
          )}
        </button>

        <p className="text-[10px] text-slate-400 text-center font-medium">
          Secure AES-256 Encryption • SC HB 3431 Protocol
        </p>
      </form>
    </div>
  );
};
